import { defineStore } from "pinia";
import { computed, nextTick, reactive, ref, watch } from "vue";
import { ROOM_SIDE_CORRIDOR, ROOM_SIDE_ROOM } from "../constants/roomSides";
import { sendConfiguratorEvent } from '../common/event-tracking';
import { pickFields } from '../common/pick-fields';

export const useConfiguratorStore = defineStore("configuration", () => {
    const isImageLoading = ref(false);
    const configHash = ref(null);
    const isInitialized = ref(false);

    function toggleRoomSide() {
        configuration.roomSide =
            configuration.roomSide === ROOM_SIDE_CORRIDOR
                ? ROOM_SIDE_ROOM
                : ROOM_SIDE_CORRIDOR;
    }

    const options = reactive({
        type: [],
        system: [],
        model: [],
        material: [],
        knock: [],
        room: {
            [ROOM_SIDE_ROOM]: [],
            [ROOM_SIDE_CORRIDOR]: [],
        },
        floor: {
            [ROOM_SIDE_ROOM]: [],
            [ROOM_SIDE_CORRIDOR]: [],
        },
        filters: {
            material: {
                holzArt: [],
                oberflaechenBeschichtung: [],
                tuerModell: [],
            },
            knock: {
                material: [],
                rosettenArt: []
            }
        }
    });

    const materialFilters = reactive({
        holzArt: '',
        oberflaechenBeschichtung: '',
        tuerModell: '',
    })

    const knockFilters = reactive({
        material: '',
        rosettenArt: '',
    })

    const defaultTypeId = ref(null);
    const defaultConfigurationsByTypeId = reactive({});

    const configuration = reactive({
        room: {
            [ROOM_SIDE_CORRIDOR]: {
                id: null,
                room: null,
            },
            [ROOM_SIDE_ROOM]: {
                id: null,
                room: null,
            },
        },
        floor: {
            [ROOM_SIDE_CORRIDOR]: {
                id: null,
                floor: null,
            },
            [ROOM_SIDE_ROOM]: {
                id: null,
                floor: null,
            },
        },
        type: {
            id: null,
            objektKategorie: null,
            title: null,
        },
        system: {
            id: null,
            stockModell: null,
            verkleidungsStoss: null,
            title: null,
        },
        model: {
            id: null,
            falzId: null,
            title: null,
        },
        material: {
            id: null,
            oberflaeche: null,
            oberflaechenBeschichtungen: null,
            holzArten: null,
            tuerModelle: null,
            title: null,
        },
        knock: {
            id: null,
            druecker: null,
            rosettenArt: null,
            material: null,
            modellId: null,
            title: null,
        },
        roomSide: null,
    });

    const jsonSerializedConfiguration = computed(() => {
        const tmp = {};

        Object.keys(configuration).forEach((key) => {
            const value = configuration[key];

            if (["room", "floor"].includes(key)) {
                tmp[key] = {
                    G: JSON.stringify(value["G"]),
                    R: JSON.stringify(value["R"]),
                };
                return;
            }

            if (typeof value === "object") {
                tmp[key] = JSON.stringify(value);
                return;
            }

            tmp[key] = value;
        });

        return JSON.stringify(tmp);
    });

    const imageSet = reactive({
        hinge: {
            mobile: null,
            desktop: null,
        },
        handle: {
            mobile: null,
            desktop: null,
        },
        door: {
            mobile: null,
            desktop: null,
        },
        stage: {
            mobile: null,
            desktop: null,
        },
    });

    watch(materialFilters, () => {
        if(!isOptionPossible(configuration.material, materialOptions.value)){
            configuration.material = materialOptions.value[0]
        }
    })

    watch(knockFilters, () => {
        if(!isOptionPossible(configuration.knock, knockOptions.value)){
            configuration.knock = knockOptions.value[0]
        }
    })

    watch(() => configuration.type.id, (newId) => {
        if(isInitialized.value){
            console.log(`APPLY DEFAULT ${newId}`);
            applyDefaultConfiguration(newId);
        }
    })

    watch(configuration, () => {
        if(isInitialized.value){
            console.log('CONFIGURATION CHANGE');
            ensurePossibleConfiguration();
            updateImageSet();
            sendChosenOptionEvents();
        }
    })

    function ensurePossibleConfiguration(){
        if(!isOptionPossible(configuration.system, systemOptions.value)){
            configuration.system = systemOptions.value[0]
        }

        if(!isOptionPossible(configuration.model, modelOptions.value)){
            configuration.model = modelOptions.value[0]
        }

        if(!isOptionPossible(configuration.material, materialOptions.value)){
            configuration.material = materialOptions.value[0]
        }

        if(!isOptionPossible(configuration.knock, knockOptions.value)){
            configuration.knock = knockOptions.value[0]
        }

        const roomOptions = configuration.roomSide === ROOM_SIDE_ROOM ? roomOptionsRoom.value : roomOptionsCorridor.value;
        if(!isOptionPossible(configuration.room[configuration.roomSide], roomOptions)){
            configuration.room[configuration.roomSide] = roomOptions[0]
        }

        const floorOptions = configuration.roomSide === ROOM_SIDE_ROOM ? floorOptionsRoom.value : floorOptionsCorridor.value;
        if(!isOptionPossible(configuration.floor[configuration.roomSide], floorOptions)){
            configuration.floor[configuration.roomSide] = floorOptions[0]
        }
    }

    function isOptionPossible(option, validOptions){
        return validOptions.some(valid => valid.id === option.id);
    }

    async function updateImageSet() {
        isImageLoading.value = true;

        try {
            const response = await fetch("/configurator/image-set", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: jsonSerializedConfiguration.value,
            });

            const json = await response.json();
            if (json.success) {
                Object.keys(imageSet).forEach((key) => {
                    if (!json.data.imageSet[key]) {
                        return;
                    }

                    const image = json.data.imageSet[key];

                    imageSet[key].desktop = image.desktop;
                    imageSet[key].mobile = image.mobile;
                });
            }
        } catch (error) {
            console.error("Unable to load image set: " + error);
        }

        isImageLoading.value = false;
    }

    function sendChosenOptionEvents(){
        try {
            sendConfiguratorEvent('choosen_option', 'raum.seite', configuration.roomSide)
            sendConfiguratorEvent('choosen_option', 'raum.boden_gangseite', configuration.floor.G)
            sendConfiguratorEvent('choosen_option', 'raum.boden_raumseite', configuration.floor.R)
            sendConfiguratorEvent('choosen_option', 'raum.type_gangseite', configuration.room.R)
            sendConfiguratorEvent('choosen_option', 'raum.type_raumseite', configuration.room.G)

            sendConfiguratorEvent('choosen_option', 'druecker.modell', configuration.knock.druecker)
            sendConfiguratorEvent('choosen_option', 'druecker.rosettenArt', configuration.knock.rosettenArt)
            sendConfiguratorEvent('choosen_option', 'druecker.material', configuration.knock.material)
            sendConfiguratorEvent('choosen_option', 'druecker.id', configuration.knock.modellId)

            sendConfiguratorEvent('choosen_option', 'material.oberflaeche', configuration.material.oberflaeche)
            sendConfiguratorEvent('choosen_option', 'material.tuerModell', configuration.material.tuerModell)
            sendConfiguratorEvent('choosen_option', 'material.oberflaechenBeschichtung', configuration.material.oberflaechenBeschichtung)
            sendConfiguratorEvent('choosen_option', 'material.holzArt', configuration.material.holzArt)

            sendConfiguratorEvent('choosen_option', 'system.stockModell', configuration.system.stockModell)
            sendConfiguratorEvent('choosen_option', 'system.verkleidungsStoss', configuration.system.verkleidungsStoss)
            sendConfiguratorEvent('choosen_option', 'system.title', configuration.system.title)

            sendConfiguratorEvent('choosen_option', 'type.title', configuration.type.title)
        } catch (e) {
        }
    }

    function init(){
        updateImageSet();
        nextTick(() => {
            isInitialized.value = true;
        });
    }

    function loadInitialData(json) {
        // Load options
        Object.keys(options).forEach((key) => {
            options[key] = json.options[key];
        });

        // Load default type
        if(json.defaultTypeId){
            defaultTypeId.value = json.defaultTypeId;
        }

        // Load default configurations
        if(json.defaultConfigurationsByTypeId){
            Object.keys(json.defaultConfigurationsByTypeId).forEach((type) => {
                defaultConfigurationsByTypeId[type] = json.defaultConfigurationsByTypeId[type];
            });
        }

        // Apply default configuration
        applyDefaultConfiguration();

        // Load configuration data
        if(json.configurationData){
            // Apply directly
            (['roomSide', 'room', 'floor']).forEach(key => {
                if(json.configurationData[key]){
                    configuration[key] = json.configurationData[key];
                }
            });

            // Load by id
            (['type', 'system', 'model', 'material', 'knock']).forEach(key => {
                configuration[key] = pickFields(findOptionById(options[key], json.configurationData[key]), Object.keys(configuration[key]))
            });
        }
    }

    function applyDefaultConfiguration(typeId = null){
        if(typeId === null){
            typeId = defaultTypeId.value;
        }

        const defaultConfiguration = defaultConfigurationsByTypeId[typeId];
        if(!defaultConfiguration){
            return;
        }

        const roomSide = defaultConfiguration.roomSide;
        if(roomSide){
            configuration.roomSide = roomSide;
            configuration.floor[roomSide] = pickFields(findOptionById(options.floor[roomSide], defaultConfiguration.floor), Object.keys(configuration.floor[roomSide]));
            configuration.room[roomSide] = pickFields(findOptionById(options.room[roomSide], defaultConfiguration.room), Object.keys(configuration.room[roomSide]));
        }

        configuration.type = pickFields(findOptionById(options.type, typeId), Object.keys(configuration.type));
        configuration.system = pickFields(findOptionById(options.system, defaultConfiguration.system), Object.keys(configuration.system));
        configuration.model = pickFields(findOptionById(options.model, defaultConfiguration.model), Object.keys(configuration.model));
        configuration.material = pickFields(findOptionById(options.material, defaultConfiguration.material), Object.keys(configuration.material));
        configuration.knock = pickFields(findOptionById(options.knock, defaultConfiguration.knock), Object.keys(configuration.knock));
    }

    function findOptionById(options, id){
        return options.find(option => option.id === id);
    }

    // Facetted main options
    const systemOptions = computed(() => {
        return filterOptions(
            options.system,
            {
                'objektKategorien': configuration.type.objektKategorie
            }
        );
    })

    const modelOptions = computed(() => {
        return filterOptions(
            options.model,
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell
            }
        );
    })

    const materialOptions = computed(() => {
        return filterOptions(
            options.material,
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell,
                'falzIds': configuration.model.falzId,

                'holzArten': materialFilters.holzArt,
                'oberflaechenBeschichtungen': materialFilters.oberflaechenBeschichtung,
                'tuerModelle': materialFilters.tuerModell,
            }
        );
    })

    const knockOptions = computed(() => {
        return filterOptions(
            options.knock,
            {
                'falzIds': configuration.model.falzId,

                'materialien': knockFilters.material,
                'rosettenArten': knockFilters.rosettenArt,
            }
        );
    })

    // Facetted filter options
    const filterHolzArtOptions = computed(() => {
        const candidates = options.filters.material.holzArt;
        const materialOptions = filterOptions(
            options.material,
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell,
                'falzIds': configuration.model.falzId,
                'oberflaechenBeschichtungen': materialFilters.oberflaechenBeschichtung,
                'tuerModelle': materialFilters.tuerModell,
            }
        );

        return candidates.filter(candidate => {
            return candidate.value === "" || materialOptions.some(option => {
                return option.holzArten.includes(candidate.value);
            })
        })
    })

    const filterOberflachenBeschichtungOptions = computed(() => {
        const candidates = options.filters.material.oberflaechenBeschichtung;
        const materialOptions = filterOptions(
            options.material,
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell,
                'falzIds': configuration.model.falzId,
                'holzArten': materialFilters.holzArt,
                'tuerModelle': materialFilters.tuerModell,
            }
        );

        return candidates.filter(candidate => {
            return candidate.value === "" || materialOptions.some(option => {
                return option.oberflaechenBeschichtungen.includes(candidate.value);
            })
        })
    })

    const filterTuerModellOptions = computed(() => {
        const candidates = options.filters.material.tuerModell;
        const materialOptions = filterOptions(
            options.material,
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell,
                'falzIds': configuration.model.falzId,
                'oberflaechenBeschichtungen': materialFilters.oberflaechenBeschichtung,
                'holzArten': materialFilters.holzArt,
            }
        );

        return candidates.filter(candidate => {
            return candidate.value === "" || materialOptions.some(option => {
                return option.tuerModelle.includes(candidate.value);
            })
        })
    })

    const filterMaterialOptions = computed(() => {
        const candidates = options.filters.knock.material;
        const knockOptions = filterOptions(
            options.knock,
            {
                'falzIds': configuration.model.falzId,
                'rosettenArt': knockFilters.rosettenArt,
            }
        );

        return candidates.filter(candidate => {
            return candidate.value === "" || knockOptions.some(option => {
                return option.material === candidate.value;
            })
        })
    })

    const filterRosettenArtOptions = computed(() => {
        const candidates = options.filters.knock.rosettenArt;
        const knockOptions = filterOptions(
            options.knock,
            {
                'falzIds': configuration.model.falzId,
                'material': knockFilters.material,
            }
        );

        return candidates.filter(candidate => {
            return candidate.value === "" || knockOptions.some(option => {
                return option.rosettenArt === candidate.value;
            })
        })
    })

    // Room and floor options
    const roomOptionsCorridor = computed(() => {
        return filterOptions(
            options.room[ROOM_SIDE_CORRIDOR],
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell,
            }
        );
    })

    const roomOptionsRoom = computed(() => {
        return filterOptions(
            options.room[ROOM_SIDE_ROOM],
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell,
            }
        );
    })

    const floorOptionsCorridor = computed(() => {
        return filterOptions(
            options.floor[ROOM_SIDE_CORRIDOR],
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell,
            }
        );
    })

    const floorOptionsRoom = computed(() => {
        return filterOptions(
            options.floor[ROOM_SIDE_ROOM],
            {
                'objektKategorien': configuration.type.objektKategorie,
                'stockModelle': configuration.system.stockModell,
            }
        );
    })

    function filterOptions(candidates, filters){
        return candidates.filter(candidate => {
            for(const key in filters){
                if(filters[key] && !candidate[key].includes(filters[key])){
                    return false;
                }
            }
            return true;
        })
    }

    return {
        isImageLoading,
        init,

        toggleRoomSide,
        options,
        configuration,
        loadInitialData,
        imageSet,
        configuration,
        configHash,
        jsonSerializedConfiguration,
        applyDefaultConfiguration,

        materialFilters,
        knockFilters,

        systemOptions,
        modelOptions,
        materialOptions,
        knockOptions,

        filterHolzArtOptions,
        filterMaterialOptions,
        filterOberflachenBeschichtungOptions,
        filterRosettenArtOptions,
        filterTuerModellOptions,

        roomOptionsCorridor,
        roomOptionsRoom,
        floorOptionsCorridor,
        floorOptionsRoom
    };
});
